import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';

import ServicesTypeForm from './ServicesTypeForm';
import { useServicesTypes } from '../../../hooks/administration/useServicesTypes';
import { useAuthContext } from '../../../context/AuthProvider';

const ServicesTypesDashboard = () => {
    const [open, setOpen] = useState(false);
    const [editTypeService, setEditTypeService] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');

    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedTypeService, setSelectedTypeService] = useState(null);

    const { user } = useAuthContext();
    const {
        servicesTypes = [],
        isLoading,
        createMutation,
        updateMutation,
        deleteMutation,
    } = useServicesTypes();

    const showAlert = useCallback((message, severity = 'success') => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditTypeService(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((typeService) => {
        setEditTypeService(typeService);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditTypeService(null);
    }, []);

    const handleOpenConfirm = useCallback((typeService) => {
        setSelectedTypeService(typeService);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedTypeService(null);
    }, []);

    const handleSubmitRoute = useCallback(
        (data) => {
            if (!user) {
                showAlert('Debe iniciar sesión para realizar esta acción', 'error');
                return;
            }

            if (editTypeService) {
                updateMutation.mutate(
                    {
                        id: editTypeService.tipserid,
                        data: {
                            ...data,
                            updbyid: user.EmpID,
                        },
                    },
                    {
                        onSuccess: () => {
                            showAlert('Tipo de servicio actualizado correctamente');
                            handleCloseForm();
                        },
                        onError: (err) =>
                            showAlert(err?.message || 'Error al actualizar el tipo de servicio', 'error'),
                    }
                );
            } else {
                createMutation.mutate(
                    {
                        ...data,
                        crebyid: user.EmpID,
                    },
                    {
                        onSuccess: () => {
                            showAlert('Tipo de servicio creado con éxito');
                            handleCloseForm();
                        },
                        onError: (err) =>
                            showAlert(err?.message || 'Error al crear el tipo de servicio', 'error'),
                    }
                );
            }
        },
        [user, editTypeService, updateMutation, createMutation, showAlert, handleCloseForm]
    );

    const handleConfirmDelete = useCallback(() => {
        if (!selectedTypeService) return;

        deleteMutation.mutate(selectedTypeService.tipserid, {
            onSuccess: () => {
                showAlert('Tipo de servicio eliminado con éxito');
                handleCloseConfirm();
            },
            onError: (err) => {
                showAlert(err?.message || 'Error al eliminar el tipo de servicio', 'error');
                handleCloseConfirm();
            },
        });
    }, [selectedTypeService, deleteMutation, showAlert, handleCloseConfirm]);

    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = servicesTypes;

        if (statusFilter === 'A') {
            data = data.filter((s) => s.estreg === 'A');
        } else if (statusFilter === 'NOT_A') {
            data = data.filter((s) => s.estreg !== 'A');
        }

        if (normalizedSearch) {
            data = data.filter((s) => {
                const nom = (s.tipsernom || '').toLowerCase();
                const des = (s.tipserdes || '').toLowerCase();

                return nom.includes(normalizedSearch) || des.includes(normalizedSearch);
            });
        }

        return data;
    }, [servicesTypes, statusFilter, normalizedSearch]);

    const columns = useMemo(
        () => [
            {
                field: 'tipsernom',
                headerName: 'Tipo de Servicio',
                flex: 1,
                minWidth: 240,
                valueGetter: (params) => params.row?.tipsernom || '—',
            },
            {
                field: 'tipserdes',
                headerName: 'Descripción',
                flex: 1.5,
                minWidth: 300,
                valueGetter: (params) => params.row?.tipserdes || '—',
            },
            {
                field: 'estreg',
                headerName: 'Estado',
                width: 130,
                renderCell: (params) => {
                    const isActive = params.value === 'A';

                    return (
                        <Chip
                            label={isActive ? 'ACTIVO' : 'INACTIVO'}
                            color={isActive ? 'success' : 'warning'}
                            size="small"
                            variant="outlined"
                        />
                    );
                },
            },
            {
                field: 'actions',
                headerName: 'Acciones',
                width: 120,
                sortable: false,
                filterable: false,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => handleOpenEdit(params.row)}>
                                <EditIcon color="primary" fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Eliminar">
                            <IconButton
                                size="small"
                                onClick={() => handleOpenConfirm(params.row)}
                                disabled={deleteMutation.isPending}
                            >
                                <DeleteIcon color="error" fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        [handleOpenEdit, handleOpenConfirm, deleteMutation.isPending]
    );

    return (
        <Paper
            sx={{
                p: 2.5,
                borderRadius: 2,
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                backgroundColor: '#f9f9f9',
            }}
        >
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={700}>
                        Tipos de Servicio
                    </Typography>

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={statusFilter}
                        onChange={(_, val) => val && setStatusFilter(val)}
                    >
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="A">Activos</ToggleButton>
                        <ToggleButton value="NOT_A">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small"
                    label="Buscar por nombre o descripción..."
                    variant="outlined"
                    fullWidth
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar">
                                    <IconButton size="small" onClick={() => setSearch('')}>
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={isLoading}
                        getRowId={(row) => row.tipserid}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } },
                            sorting: { sortModel: [{ field: 'tipsernom', sort: 'asc' }] },
                        }}
                        sx={{
                            borderRadius: 2,
                            backgroundColor: '#fff',
                            '& .MuiDataGrid-columnHeaders': {
                                fontWeight: 700,
                                backgroundColor: '#f0f0f0',
                            },
                        }}
                    />
                </Box>
            </Stack>

            <Fab
                color="primary"
                aria-label="add"
                sx={{ position: 'fixed', bottom: 20, right: 20 }}
                onClick={handleOpenCreate}
            >
                <AddIcon />
            </Fab>

            {open && (
                <ServicesTypeForm
                    open={open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitRoute}
                    defaultValues={editTypeService || {}}
                />
            )}

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el tipo de servicio{' '}
                        <strong>{selectedTypeService?.tipsernom}</strong>?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">
                        Cancelar
                    </Button>

                    <Button
                        onClick={handleConfirmDelete}
                        variant="contained"
                        color="error"
                        disabled={deleteMutation.isPending}
                    >
                        {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={alert.open}
                autoHideDuration={4000}
                onClose={() => setAlert((a) => ({ ...a, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={() => setAlert((a) => ({ ...a, open: false }))}
                    severity={alert.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ServicesTypesDashboard;